import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardText,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import Header from "../../../components/Headers/Header";
import Image from "../../../components/utils/Image";
import DotsSpinner from "../../../components/Loader/DotsSpinner/DotsSpinner";

import "./ticket.scss";

import { TypeComment, TypeTicketDetails } from "../../../types";
import {
  closePendingTicket,
  getComments,
  getTicketDetails,
  postNewComment,
} from "../../../Services/Ticket/ticketService";
import ConfirmModal from "../../../components/Modals/ConfirmModal";

export default function TicketDetails() {
  const { id } = useParams();
  const userInformation =
    useSelector((state: any) => state.user.userInformation) || {};

  const scroll = useRef<HTMLDivElement>();

  // for comments
  const [modal, setModal] = useState(true);
  const toggle = () => setModal(!modal);
  // for ticket close functionality
  const [confirmModal, setConfirmModal] = useState(false);
  const confirmToggle = () => setConfirmModal(!confirmModal);

  const [ticketDetails, setTicketDetails] = useState<TypeTicketDetails>(); // stores ticket details based on parameter(id)
  const [comments, setComments] = useState<TypeComment[]>([]); // stores comments for current ticket
  const [adminComment, setAdminComment] = useState<string>(""); // stores admin comment

  const [isLoading, setIsLoading] = useState<Boolean>(false);

  useEffect(() => {
    console.log("Ticket id: ", id, "Admin id: ", userInformation?.id);
    getParticularTicketDetails(id);
    getCommentsForTicket(id);

    const timer = setInterval(() => {
      getCommentsForTicket(id);
    }, 3000);

    return () => {
      clearInterval(timer);
    };
  }, [id]);
  useEffect(() => {
    setTimeout(() => {
      modal && scroll.current?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  }, [modal]);

  // get ticket/grievance details
  const getParticularTicketDetails = (id: number) => {
    getTicketDetails(id).then(
      (response: AxiosResponse) => {
        // console.log("Response for ticket details: ", response?.data?.data);
        setTicketDetails(response?.data?.data);
      },
      (error) => {
        console.log("Error caught: ", error?.response);
        toast.error("Could not fetch ticket details!");
      }
    );
  };

  // get comments for current ticket/grievance
  const getCommentsForTicket = (id: number) => {
    getComments(id).then(
      (response: AxiosResponse) => {
        console.log("Response for comments: ", response?.data?.data);
        setComments(response?.data?.data);
      },
      (error) => {
        console.log("Error caught: ", error?.response);
      }
    );
  };

  // send comment
  const sendComment = (e, target: 0 | 1) => {
    // console.log("key: ", e.key, "value: ", e.target.value);
    // console.log(adminComment)
    if (e.key === "Enter" && !target) {
      if (adminComment?.length) {
        let comment = {
          user: userInformation?.id,
          text: adminComment,
          ticket: ticketDetails?.id,
        };
        postNewComment(comment).then(
          (response: AxiosResponse) => {
            // console.log("Response for add comment: ", response?.data?.data);
            setAdminComment("");
            getCommentsForTicket(id);
          },
          (error) => {
            console.log("Error caught: ", error?.response);
            toast.error("Could not add the comment!");
          }
        );
      }
    } else {
      if (adminComment?.length && target) {
        let comment = {
          user: userInformation?.id,
          text: adminComment,
          ticket: ticketDetails?.id,
        };
        postNewComment(comment).then(
          (response: AxiosResponse) => {
            console.log("Response for add comment: ", response?.data?.data);
            setAdminComment("");
            getCommentsForTicket(id);
          },
          (error) => {
            console.log("Error caught: ", error?.response);
            toast.error("Could not add the comment!");
          }
        );
      }
    }
  };

  // change status to 'closed' for current ticket
  const closeCurrentTicket = () => {
    setIsLoading(true);
    confirmToggle();
    closePendingTicket(id)
      .then(
        (response: AxiosResponse) => {
          // console.log("Response for ticket close: ", response?.data?.data);
          getParticularTicketDetails(id);
          confirmToggle();
        },
        (error) => {
          console.log("Error caught: ", error?.response);
          toast.error("Could not close this ticket!");
        }
      )
      .finally(() => setIsLoading(false));
  };

  // scroll to bottom
  const scrollToBottom = () => {
    scroll.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <React.Fragment>
      <Header />

      {/* Page content */}

      <Container className="mt--7" fluid>
        <Card className="shadow">
          {" "}
          {/* bg-gradient-default */}
          <CardHeader className="bg-transparent border-0">
            <Row className="align-items-center">
              <div className="col">
                <h3 className="mb-0 text-muted">
                  <i className="fas fa-clipboard-list mr-1"></i>Ticket Details
                </h3>
              </div>
              <div className="col"></div>
            </Row>
          </CardHeader>
          <CardBody>
            {ticketDetails?.id ? (
              <React.Fragment>
                {/* TICKET INFO */}
                <Row>
                  <Col lg="9" mg="7" sm="12">
                    <div>
                      <Label className="small text-muted mb-0 font-weight-bold">
                        Subject:{" "}
                      </Label>
                      <p>{ticketDetails?.subject?.name}</p>
                    </div>
                    <div>
                      <Label className="small text-muted mb-0 font-weight-bold">
                        Query:{" "}
                      </Label>
                      <p>{ticketDetails?.query}</p>
                    </div>
                    <div>
                      <Label className="small text-muted mb-0 font-weight-bold">
                        Status:{" "}
                      </Label>
                      <p className="d-flex align-items-center text-capitalize">
                        <i
                          className={`fas fa-dot-circle mr-1 + ${
                            ticketDetails?.status === "pending"
                              ? "text-warning"
                              : "text-success"
                          }`}
                        ></i>
                        {ticketDetails?.status}
                      </p>
                    </div>
                  </Col>
                  <Col lg="3" mg="5" sm="12" className="text-center">
                    <Image
                      source={
                        ticketDetails?.user?.avatar?.data?.thumbnails[2]?.url
                      }
                      size={100}
                      classes={"rounded"}
                    />
                    <div>
                      <span>
                        {ticketDetails?.user?.first_name +
                          " " +
                          ticketDetails?.user?.last_name}
                      </span>
                      {/* <p className="small">{ticketDetails?.user?.srn_no}</p> */}
                    </div>
                  </Col>
                </Row>

                <div>
                  {/* <Button color="info" size="sm" onClick={toggle}>Comments</Button> */}
                  {ticketDetails?.status !== "closed" ? (
                    !isLoading ? (
                      <Button color="danger" size="sm" onClick={confirmToggle}>
                        Close Ticket
                      </Button>
                    ) : (
                      <Button color="danger" size="sm" className="mt-3 mx-3">
                        <div
                          className="spinner-border rounded-circle spinner-border-sm"
                          role="status"
                        >
                          <p className="sr-only">Loading...</p>
                        </div>
                      </Button>
                    )
                  ) : (
                    <></>
                  )}

                  {/* CONFIRMATION MODAL FOR TICKET CLOSE */}
                  <ConfirmModal
                    isOpen={confirmModal}
                    onClose={() => setConfirmModal(false)}
                    type={"add"}
                    title={`Are you sure you want to close this ticket?`}
                    onConfirm={() => {
                      setConfirmModal(false);
                      closeCurrentTicket();
                    }}
                  />

                  {/* COMMENT MODAL */}
                  {/* <Modal isOpen={modal} toggle={toggle} className={""} size="lg">
                                    <ModalHeader toggle={toggle} className="text-muted">Comments</ModalHeader>
                                    <ModalBody>
                                        <div style={{ maxHeight: 360, overflowX: 'hidden' }}>
                                            {
                                                comments?.length !== 0 ? comments?.map((each: TypeComment, index) => (
                                                    <Row key={index}>
                                                        <Col sm={12} className="w-100">
                                                            <div
                                                                className={`w-75 mx-2 ${each?.user === ticketDetails?.user?.id ? '' : 'float-right'}`}
                                                            >
                                                                <Card
                                                                    body
                                                                    inverse
                                                                    className={`my-2 py-1 comment-box ${each?.user === ticketDetails?.user?.id ? 'user-comment' : 'admin-comment'}`}
                                                                    style={{ borderRadius: 20 }}
                                                                >
                                                                    <div className="w-100">
                                                                        <span
                                                                            className={`text-white small p-0 mx-0 ${each?.user === ticketDetails?.user?.id ? '' : 'float-right'}`}
                                                                        >
                                                                            {each?.user === ticketDetails?.user?.id && <i className="fas fa-caret-right mr-1"></i>}
                                                                            {each?.user === ticketDetails?.user?.id ? ticketDetails?.user?.first_name : userInformation?.first_name}
                                                                            {each?.user !== ticketDetails?.user?.id && <i className="fas fa-caret-left ml-1"></i>}
                                                                        </span>
                                                                    </div>
                                                                    <CardText className="d-flex justify-content-start">{each?.text}</CardText>
                                                                    <div className="d-flex justify-content-end">
                                                                        <span className="small text-white ">{moment(each?.created_on).format("MMM Do YYYY, hh:mm a")}</span>
                                                                    </div>
                                                                </Card>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )) : (
                                                    <div className="d-flex justify-content-center align-items-center text-muted font-weight-bold">No comment available!</div>
                                                )
                                            }
                                            <div ref={scroll}></div>
                                        </div>
                                    </ModalBody>
                                    {ticketDetails?.status !== 'closed' ? (<ModalFooter className="pt-0">
                                        <InputGroup>
                                            <Input
                                                autoFocus
                                                placeholder="comment ..."
                                                value={adminComment}
                                                onChange={(e) => setAdminComment(e.target.value)}
                                                onKeyUp={(e) => sendComment(e, 0)}
                                            />
                                            <InputGroupAddon addonType="append">
                                                <InputGroupText
                                                    className="pointer"
                                                    onClick={(e) => sendComment(e, 1)}
                                                ><i className="fas fa-paper-plane"></i></InputGroupText>
                                            </InputGroupAddon>
                                        </InputGroup>
                                    </ModalFooter>) : <></>}
                                </Modal> */}
                </div>
              </React.Fragment>
            ) : (
              <DotsSpinner />
            )}
          </CardBody>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <h5 className="mb-0 text-muted">Comments</h5>
          </CardHeader>
          <CardBody>
            <div style={{ maxHeight: 360, overflowX: "hidden" }}>
              {comments?.length !== 0 ? (
                comments?.map((each: TypeComment, index) => (
                  <Row key={index}>
                    <Col sm={12} className="w-100">
                      <div
                        className={`w-75 mx-2 ${
                          each?.user === ticketDetails?.user?.id
                            ? ""
                            : "float-right"
                        }`}
                      >
                        <Card
                          body
                          inverse
                          className={`my-2 py-1 comment-box ${
                            each?.user === ticketDetails?.user?.id
                              ? "user-comment"
                              : "admin-comment"
                          }`}
                          style={{ borderRadius: 20 }}
                        >
                          <div className="w-100">
                            <span
                              className={`text-white small p-0 mx-0 ${
                                each?.user === ticketDetails?.user?.id
                                  ? ""
                                  : "float-right"
                              }`}
                            >
                              {each?.user === ticketDetails?.user?.id && (
                                <i className="fas fa-caret-right mr-1"></i>
                              )}
                              {each?.user === ticketDetails?.user?.id
                                ? ticketDetails?.user?.first_name
                                : userInformation?.first_name}
                              {each?.user !== ticketDetails?.user?.id && (
                                <i className="fas fa-caret-left ml-1"></i>
                              )}
                            </span>
                          </div>
                          <CardText className="d-flex justify-content-start">
                            {each?.text}
                          </CardText>
                          <div className="d-flex justify-content-end">
                            <span className="small text-white ">
                              {moment(each?.created_on).format(
                                "MMM Do YYYY, hh:mm a"
                              )}
                            </span>
                          </div>
                        </Card>
                      </div>
                    </Col>
                  </Row>
                ))
              ) : (
                <div className="d-flex justify-content-center align-items-center text-muted font-weight-bold">
                  No comment available!
                </div>
              )}
              <div ref={scroll}></div>
            </div>
            {ticketDetails?.status !== "closed" ? (
              <div className="pt-1">
                <InputGroup>
                  <Input
                    autoFocus
                    placeholder="comment ..."
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    onKeyUp={(e) => sendComment(e, 0)}
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="pointer"
                      onClick={(e) => sendComment(e, 1)}
                    >
                      <i className="fas fa-paper-plane"></i>
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            ) : (
              <></>
            )}
          </CardBody>
        </Card>
      </Container>
    </React.Fragment>
  );
}
